import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';

// Constants
const STORAGE_KEY = 'aps_referral_code';
const STORAGE_SOURCE_KEY = 'aps_referral_source';
const STORAGE_TIMESTAMP_KEY = 'aps_referral_timestamp';
const CODE_EXPIRY_DAYS = 30;

interface ReferralInfo {
  hasReferralCode: boolean;
  isLocked: boolean;
  code: string | null;
  codeDescription: string | null;
  partnerName: string | null;
  partnerCompany: string | null;
  appliedAt: string | null;
  lockedAt: string | null;
  source: string | null;
}

interface ReferralContextType {
  // Current state
  referralInfo: ReferralInfo | null;
  pendingCode: string | null;
  pendingSource: string | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  captureCode: (code: string, source: string) => void;
  applyCode: (code: string, source?: string) => Promise<{ success: boolean; message: string }>;
  removeCode: () => Promise<{ success: boolean; message: string }>;
  clearPendingCode: () => void;
  refreshReferralInfo: () => Promise<void>;
  validateCode: (code: string) => Promise<{ valid: boolean; partnerName?: string; error?: string }>;
}

const ReferralContext = createContext<ReferralContextType | undefined>(undefined);

export const useReferral = (): ReferralContextType => {
  const context = useContext(ReferralContext);
  if (!context) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
};

interface ReferralProviderProps {
  children: ReactNode;
}

export const ReferralProvider: React.FC<ReferralProviderProps> = ({ children }) => {
  const { isAuthenticated, sessionToken } = useAuth();
  
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [pendingSource, setPendingSource] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load pending code from localStorage on mount
  useEffect(() => {
    const storedCode = localStorage.getItem(STORAGE_KEY);
    const storedSource = localStorage.getItem(STORAGE_SOURCE_KEY);
    const storedTimestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
    
    if (storedCode && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10);
      const now = Date.now();
      const expiryMs = CODE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      
      if (now - timestamp < expiryMs) {
        setPendingCode(storedCode);
        setPendingSource(storedSource || 'unknown');
      } else {
        // Code expired, clear storage
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_SOURCE_KEY);
        localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      }
    }
  }, []);

  // Check URL for referral code on mount (deep link handling)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get('ref') || urlParams.get('referral');
    
    if (codeFromUrl) {
      captureCode(codeFromUrl, 'deep_link');
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      url.searchParams.delete('referral');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Fetch referral info when authenticated
  useEffect(() => {
    if (isAuthenticated && sessionToken) {
      refreshReferralInfo();
    } else {
      setReferralInfo(null);
    }
  }, [isAuthenticated, sessionToken]);

  const captureCode = useCallback((code: string, source: string) => {
    const normalizedCode = code.toUpperCase().trim();
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, normalizedCode);
    localStorage.setItem(STORAGE_SOURCE_KEY, source);
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
    
    setPendingCode(normalizedCode);
    setPendingSource(source);
    setError(null);
  }, []);

  const clearPendingCode = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_SOURCE_KEY);
    localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
    setPendingCode(null);
    setPendingSource(null);
  }, []);

  const validateCode = useCallback(async (code: string): Promise<{ valid: boolean; partnerName?: string; error?: string }> => {
    try {
      const response = await fetch(`https://apsrecruiting.io/api/referral/validate/${code.toUpperCase()}`);
      const data = await response.json();
      
      if (response.ok && data.valid) {
        return { valid: true, partnerName: data.partnerName };
      }
      
      return { valid: false, error: data.error?.message || 'Invalid code' };
    } catch (err) {
      return { valid: false, error: 'Failed to validate code' };
    }
  }, []);

  const applyCode = useCallback(async (code: string, source: string = 'web'): Promise<{ success: boolean; message: string }> => {
    if (!sessionToken) {
      return { success: false, message: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('https://apsrecruiting.io/api/user/referral-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ code: code.toUpperCase(), source }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Clear pending code from storage
        clearPendingCode();
        
        // Refresh referral info
        await refreshReferralInfo();
        
        return { 
          success: true, 
          message: data.message || `Referral code ${data.code} applied successfully!` 
        };
      }

      const errorMessage = data.error?.message || 'Failed to apply referral code';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } catch (err) {
      const errorMessage = 'Failed to apply referral code. Please try again.';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken, clearPendingCode]);

  const removeCode = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    if (!sessionToken) {
      return { success: false, message: 'Not authenticated' };
    }

    if (referralInfo?.isLocked) {
      return { success: false, message: 'Cannot remove a locked referral code' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('https://apsrecruiting.io/api/user/referral-code', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await refreshReferralInfo();
        return { success: true, message: 'Referral code removed successfully' };
      }

      const errorMessage = data.error?.message || 'Failed to remove referral code';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } catch (err) {
      const errorMessage = 'Failed to remove referral code. Please try again.';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken, referralInfo?.isLocked]);

  const refreshReferralInfo = useCallback(async () => {
    if (!sessionToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('https://apsrecruiting.io/api/user/referral-code', {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReferralInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch referral info:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken]);

  // Auto-apply pending code when user becomes authenticated
  useEffect(() => {
    console.log('[ReferralContext] Auto-apply check:', {
      isAuthenticated,
      hasSessionToken: !!sessionToken,
      pendingCode,
      referralInfo,
    });
    
    if (isAuthenticated && sessionToken && pendingCode && referralInfo && !referralInfo.hasReferralCode && !referralInfo.isLocked) {
      console.log('[ReferralContext] Auto-applying pending referral code:', pendingCode);
      applyCode(pendingCode, pendingSource || 'web')
        .then(result => {
          console.log('[ReferralContext] Auto-apply result:', result);
        })
        .catch(err => {
          console.error('[ReferralContext] Auto-apply error:', err);
        });
    }
  }, [isAuthenticated, sessionToken, pendingCode, referralInfo, applyCode, pendingSource]);

  const value: ReferralContextType = {
    referralInfo,
    pendingCode,
    pendingSource,
    isLoading,
    error,
    captureCode,
    applyCode,
    removeCode,
    clearPendingCode,
    refreshReferralInfo,
    validateCode,
  };

  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
};

export default ReferralContext;
